import { useState, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderInput,
  FolderPlus,
  Home,
  LayoutGrid,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDroppable, pointerWithin, closestCenter } from '@dnd-kit/core';
import useAuthStore from '../../store/authStore';
import useBoardStore from '../../store/boardStore';
import TopHeader from './TopHeader';
import FavoritesSection from './sidebar/FavoritesSection';
import RecentSection from './sidebar/RecentSection';
import DraggableBoardItem from './sidebar/DraggableBoardItem';
import WorkspaceDropdown, { loadActiveWorkspaceId, saveActiveWorkspaceId, pushRecentWs } from './sidebar/WorkspaceDropdown';
import BoardItemMenu from './sidebar/BoardItemMenu';
import WorkspaceFolder from './sidebar/WorkspaceFolder';
import { dialog } from '../../store/dialogStore';
import useToastStore from '../../store/toastStore';
import { isOrgAdmin, canManageBoardEntry, canManageBoardProtection } from '../../utils/permissions';

const SIDEBAR_WIDTH_KEY = 'seasymon:sidebar:width';
const SIDEBAR_COLLAPSED_THRESHOLD = 80;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const SIDEBAR_MIN_EXPANDED = 180;
const SIDEBAR_MAX = 480;
const SIDEBAR_DEFAULT = 250;

function loadSidebarWidth() {
  try {
    const v = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
    if (!Number.isFinite(v) || v <= 0) return SIDEBAR_DEFAULT;
    return v;
  } catch {
    return SIDEBAR_DEFAULT;
  }
}

export default function AppLayout() {
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth);
  const [resizing, setResizing] = useState(false);
  const lastExpandedRef = useRef(sidebarWidth > SIDEBAR_COLLAPSED_THRESHOLD ? sidebarWidth : SIDEBAR_DEFAULT);
  const collapsed = sidebarWidth <= SIDEBAR_COLLAPSED_THRESHOLD;

  const [modalOpen, setModalOpen] = useState(false);
  const [activeDragBoard, setActiveDragBoard] = useState(null);
  const [activeDragFolder, setActiveDragFolder] = useState(null);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: activeBoardId } = useParams();

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth)); } catch {}
  }, [sidebarWidth]);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const x = e.clientX;
      if (x < SIDEBAR_MIN_EXPANDED - 40) {
        setSidebarWidth(SIDEBAR_COLLAPSED_WIDTH);
      } else {
        setSidebarWidth(Math.max(SIDEBAR_MIN_EXPANDED, Math.min(SIDEBAR_MAX, x)));
      }
    };
    const onUp = () => setResizing(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing]);

  const toggleCollapsed = () => {
    if (collapsed) {
      setSidebarWidth(lastExpandedRef.current);
    } else {
      lastExpandedRef.current = sidebarWidth;
      setSidebarWidth(SIDEBAR_COLLAPSED_WIDTH);
    }
  };

  const { workspaces, boards } = useBoardStore(
    useShallow((s) => ({ workspaces: s.workspaces, boards: s.boards }))
  );
  const fetchWorkspaces = useBoardStore((s) => s.fetchWorkspaces);
  const fetchBoards = useBoardStore((s) => s.fetchBoards);
  const fetchFavorites = useBoardStore((s) => s.fetchFavorites);
  const loadRecents = useBoardStore((s) => s.loadRecents);
  const addBoardToFolder = useBoardStore((s) => s.addBoardToFolder);
  const moveBoardToWorkspaceFolder = useBoardStore((s) => s.moveBoardToWorkspaceFolder);
  const reorderBoards = useBoardStore((s) => s.reorderBoards);
  const reorderRoot = useBoardStore((s) => s.reorderRoot);
  const moveFolderToWorkspace = useBoardStore((s) => s.moveFolderToWorkspace);
  const createWorkspace = useBoardStore((s) => s.createWorkspace);
  const createWorkspaceFolder = useBoardStore((s) => s.createWorkspaceFolder);
  const startSidebarPolling = useBoardStore((s) => s.startSidebarPolling);
  const stopSidebarPolling = useBoardStore((s) => s.stopSidebarPolling);
  const setSidebarDropHint = useBoardStore((s) => s.setSidebarDropHint);
  const setSidebarDragKind = useBoardStore((s) => s.setSidebarDragKind);
  const showToast = useToastStore((s) => s.show);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const customCollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
  };

  const [activeWsId, setActiveWsId] = useState(null);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const plusMenuRef = useRef(null);

  useEffect(() => {
    fetchWorkspaces();
    fetchFavorites();
  }, [fetchWorkspaces, fetchFavorites]);

  useEffect(() => {
    if (user?.id) loadRecents(user.id);
  }, [user?.id, loadRecents]);

  useEffect(() => {
    if (!user?.id) return;
    startSidebarPolling(15000);
    return () => stopSidebarPolling();
  }, [user?.id, startSidebarPolling, stopSidebarPolling]);

  // define/valida workspace ativo sempre que a lista muda ou user loga
  useEffect(() => {
    if (!user?.id || !workspaces.length) return;
    const stored = loadActiveWorkspaceId(user.id);
    const validId = workspaces.find((w) => w.id === stored)?.id || workspaces[0].id;
    if (validId !== activeWsId) setActiveWsId(validId);
    if (validId !== stored) saveActiveWorkspaceId(user.id, validId);
  }, [user?.id, workspaces]);

  const changeActiveWs = (id) => {
    setActiveWsId(id);
    if (user?.id && id) {
      saveActiveWorkspaceId(user.id, id);
      pushRecentWs(user.id, id);
    }
    // sai do board atual se ele nao pertence ao novo workspace,
    // pra evitar que o useEffect abaixo reverta a troca
    if (activeBoardId) {
      const b = boards.find((x) => x.id === Number(activeBoardId));
      if (!b || b.workspace_id !== id) navigate('/');
    }
  };

  useEffect(() => {
    if (!plusMenuOpen) return;
    const h = (e) => { if (plusMenuRef.current && !plusMenuRef.current.contains(e.target)) setPlusMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [plusMenuOpen]);

  const handleNewBoard = () => { setPlusMenuOpen(false); setModalOpen(true); };

  const handleNewFolder = async () => {
    setPlusMenuOpen(false);
    if (!activeWsId) return dialog.alert('Selecione uma área', 'Escolha uma área de trabalho antes de criar uma pasta.');
    const name = await dialog.prompt('Nova pasta', { placeholder: 'Nome da pasta', confirmLabel: 'Criar' });
    if (!name) return;
    try { await createWorkspaceFolder(activeWsId, name.trim()); }
    catch (err) { dialog.alert('Erro', err.message); }
  };

  const handleNewWorkspace = async () => {
    setPlusMenuOpen(false);
    const name = await dialog.prompt('Nova área de trabalho', { placeholder: 'Nome da área', confirmLabel: 'Criar' });
    if (!name) return;
    try {
      const ws = await createWorkspace(name.trim());
      changeActiveWs(ws.id);
    } catch (err) { dialog.alert('Erro', err.message); }
  };

  // sincroniza o workspace ativo com o board aberto — so dispara quando
  // o proprio activeBoardId muda (navegacao via Favoritos/Recentes).
  useEffect(() => {
    if (!activeBoardId || !boards.length) return;
    const b = boards.find((x) => x.id === Number(activeBoardId));
    if (b && user?.id) {
      setActiveWsId(b.workspace_id);
      saveActiveWorkspaceId(user.id, b.workspace_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBoardId]);

  // Cursor Y global durante drag (alimentado por pointermove). Necessário porque
  // dnd-kit não nos dá clientY direto em handleDragOver.
  const pointerYRef = useRef(0);
  // Hysteresis: mantém última metade entre os thresholds 40%-60% pra eliminar flicker.
  const lastHalfByBoardRef = useRef(new Map());

  // Cálculo de half com zona morta (40%-60% do item mantém último valor).
  const computeHalf = (overRect, boardId) => {
    if (!overRect) return null;
    const y = pointerYRef.current;
    const topThreshold = overRect.top + overRect.height * 0.40;
    const bottomThreshold = overRect.top + overRect.height * 0.60;
    let last = lastHalfByBoardRef.current.get(boardId) ?? null;
    let next = last;
    if (y < topThreshold) next = 'top';
    else if (y > bottomThreshold) next = 'bottom';
    // dead zone: mantém last; se nunca setou, decide por geometria pura
    if (next == null) {
      next = y < overRect.top + overRect.height / 2 ? 'top' : 'bottom';
    }
    if (next !== last) lastHalfByBoardRef.current.set(boardId, next);
    return next;
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) { setSidebarDropHint(null); return; }
    const activeData = active.data?.current;
    const overData = over.data?.current;

    // ---- Arrastando um BOARD ----
    if (activeData?.type === 'board') {
      // Sobre outro board: reorder (linha no board). Comportamento original.
      if (overData?.type === 'board-slot') {
        if (activeData.boardId === overData.boardId) { setSidebarDropHint(null); return; }
        const half = computeHalf(over.rect, `b-${overData.boardId}`);
        const position = half === 'top' ? 'before' : 'after';
        const cur = useBoardStore.getState().sidebarDropHint;
        if (cur?.boardId !== overData.boardId || cur?.position !== position) {
          setSidebarDropHint({ boardId: overData.boardId, position });
        }
        return;
      }
      // Sobre uma pasta: "dentro" (sem linha; ring tratado por isOver). Limpa hint.
      setSidebarDropHint(null);
      return;
    }

    // ---- Arrastando uma PASTA ----
    if (activeData?.type === 'folder') {
      // Sobre um board da raiz: insere a pasta antes/depois desse board.
      if (overData?.type === 'board-slot' && overData.folderId == null) {
        const half = computeHalf(over.rect, `b-${overData.boardId}`);
        const position = half === 'top' ? 'before' : 'after';
        const cur = useBoardStore.getState().sidebarDropHint;
        if (cur?.boardId !== overData.boardId || cur?.position !== position) {
          setSidebarDropHint({ boardId: overData.boardId, position });
        }
        return;
      }
      // Sobre outra pasta: insere antes/depois dela.
      if (overData?.type === 'workspaceFolder') {
        if (activeData.folderId === overData.folderId) { setSidebarDropHint(null); return; }
        const half = computeHalf(over.rect, `f-${overData.folderId}`);
        const position = half === 'top' ? 'before' : 'after';
        const cur = useBoardStore.getState().sidebarDropHint;
        if (cur?.folderId !== overData.folderId || cur?.position !== position) {
          setSidebarDropHint({ folderId: overData.folderId, position });
        }
        return;
      }
      setSidebarDropHint(null);
      return;
    }

    setSidebarDropHint(null);
  };

  const handleDragStart = (event) => {
    const activeData = event.active?.data?.current;
    if (activeData?.type === 'board') {
      const board = useBoardStore.getState().boards.find((b) => b.id === activeData.boardId);
      setActiveDragBoard(board ?? null);
      setSidebarDragKind('board');
    } else if (activeData?.type === 'folder') {
      const folder = useBoardStore.getState().workspaceFolders.find((f) => f.id === activeData.folderId);
      setActiveDragFolder(folder ?? null);
      setSidebarDragKind('folder');
    }
    lastHalfByBoardRef.current.clear();
  };

  const handleDragCancel = () => {
    setActiveDragBoard(null);
    setActiveDragFolder(null);
    setSidebarDragKind(null);
    setSidebarDropHint(null);
    lastHalfByBoardRef.current.clear();
  };

  // Monta a lista UNICA da raiz (pastas + boards soltos) ordenada por position.
  const buildRootItems = (state, workspaceId) => {
    const fids = new Set(state.workspaceFolders.filter((f) => f.workspace_id === workspaceId).map((f) => f.id));
    const items = [
      ...state.workspaceFolders
        .filter((f) => f.workspace_id === workspaceId)
        .map((f) => ({ kind: 'folder', id: f.id, position: f.position })),
      ...state.boards
        .filter((b) => b.workspace_id === workspaceId && (b.folder_id == null || !fids.has(b.folder_id)) && b.archived_at == null)
        .map((b) => ({ kind: 'board', id: b.id, position: b.position })),
    ];
    return items.sort((a, b) => {
      const pa = a.position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.position ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      return a.id - b.id;
    });
  };

  // Insere `moving` (item da lista unica) antes/depois de um alvo, retorna nova ordem.
  const reorderRootList = (rootItems, moving, targetKind, targetId, position) => {
    const without = rootItems.filter((it) => !(it.kind === moving.kind && it.id === moving.id));
    const overIndex = without.findIndex((it) => it.kind === targetKind && it.id === targetId);
    if (overIndex < 0) return null;
    const insertIndex = position === 'before' ? overIndex : overIndex + 1;
    return [
      ...without.slice(0, insertIndex),
      moving,
      ...without.slice(insertIndex),
    ];
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const lastHint = useBoardStore.getState().sidebarDropHint;
    setActiveDragBoard(null);
    setActiveDragFolder(null);
    setSidebarDragKind(null);
    setSidebarDropHint(null);
    if (!over) { lastHalfByBoardRef.current.clear(); return; }
    const overData = over.data?.current;
    const activeData = active.data?.current;
    const state = useBoardStore.getState();

    // ===== Arrastando uma PASTA: reordenar na lista unica raiz =====
    if (activeData?.type === 'folder') {
      const folder = state.workspaceFolders.find((f) => f.id === activeData.folderId);
      if (!folder) { lastHalfByBoardRef.current.clear(); return; }
      const wsId = folder.workspace_id;

      let targetKind = null, targetId = null, position = null;
      if (overData?.type === 'board-slot' && overData.folderId == null) {
        const overBoard = state.boards.find((b) => b.id === overData.boardId);
        if (!overBoard || overBoard.workspace_id !== wsId) { lastHalfByBoardRef.current.clear(); return; }
        targetKind = 'board'; targetId = overData.boardId;
        position = (lastHint?.boardId === overData.boardId)
          ? lastHint.position
          : (computeHalf(over.rect, `b-${overData.boardId}`) === 'top' ? 'before' : 'after');
      } else if (overData?.type === 'workspaceFolder') {
        if (overData.folderId === activeData.folderId) { lastHalfByBoardRef.current.clear(); return; }
        const overFolder = state.workspaceFolders.find((f) => f.id === overData.folderId);
        if (!overFolder || overFolder.workspace_id !== wsId) { lastHalfByBoardRef.current.clear(); return; }
        targetKind = 'folder'; targetId = overData.folderId;
        position = (lastHint?.folderId === overData.folderId)
          ? lastHint.position
          : (computeHalf(over.rect, `f-${overData.folderId}`) === 'top' ? 'before' : 'after');
      } else {
        lastHalfByBoardRef.current.clear();
        return;
      }

      const rootItems = buildRootItems(state, wsId);
      const previousItems = rootItems.map((it) => ({ kind: it.kind, id: it.id }));
      const newOrder = reorderRootList(rootItems, { kind: 'folder', id: activeData.folderId }, targetKind, targetId, position);
      if (!newOrder) { lastHalfByBoardRef.current.clear(); return; }
      try {
        await reorderRoot({ workspaceId: wsId, items: newOrder.map((it) => ({ kind: it.kind, id: it.id })) });
        showToast({
          message: 'Pasta reordenada',
          onUndo: async () => {
            try { await reorderRoot({ workspaceId: wsId, items: previousItems }); } catch {}
          },
        });
      } catch (err) {
        dialog.alert('Erro', err.message || 'Falha ao reordenar');
      }
      lastHalfByBoardRef.current.clear();
      return;
    }

    if (activeData?.type !== 'board') { lastHalfByBoardRef.current.clear(); return; }

    const selected = state.selectedBoardIds;
    const draggingSelected = selected.includes(activeData.boardId) && selected.length > 1;
    const ids = draggingSelected ? selected : [activeData.boardId];

    if (overData?.type === 'workspaceRoot') {
      const validIds = ids.filter((id) => {
        const b = state.boards.find((x) => x.id === id);
        return b && b.folder_id != null;
      });
      await Promise.all(validIds.map((id) => moveBoardToWorkspaceFolder(id, null)));
      return;
    } else if (overData?.type === 'folder') {
      const targetFolder = state.favoriteFolders.find((f) => f.id === overData.folderId);
      await Promise.all(
        ids
          .filter((id) => !targetFolder?.boards?.some((b) => b.id === id))
          .map((id) => addBoardToFolder(overData.folderId, id))
      );
    } else if (overData?.type === 'workspaceFolder') {
      await Promise.all(
        ids
          .filter((id) => {
            const b = state.boards.find((x) => x.id === id);
            return b && b.folder_id !== overData.folderId;
          })
          .map((id) => moveBoardToWorkspaceFolder(id, overData.folderId))
      );
    } else if (overData?.type === 'board-slot') {
      // Reorder: ativo cai antes/depois do over, no mesmo escopo do over
      if (activeData.boardId === overData.boardId) return;
      const overBoard = state.boards.find((b) => b.id === overData.boardId);
      const activeBoard = state.boards.find((b) => b.id === activeData.boardId);
      if (!overBoard || !activeBoard) return;
      // Só suportamos reorder dentro do mesmo workspace
      if (overBoard.workspace_id !== activeBoard.workspace_id) return;
      const targetFolderId = overBoard.folder_id ?? null;
      // half é decidido pelo hint global (calculado com hysteresis em handleDragOver)
      const position = (lastHint?.boardId === overData.boardId)
        ? lastHint.position
        : (computeHalf(over.rect, `b-${overData.boardId}`) === 'top' ? 'before' : 'after');

      // ----- Board na RAIZ: reordena na lista unica (pastas + boards) -----
      if (targetFolderId == null) {
        const wsId = overBoard.workspace_id;
        const cameFromFolder = activeBoard.folder_id != null;
        const originalFolderId = activeBoard.folder_id ?? null;
        // Ordem da raiz ANTES de qualquer mutacao (para o undo). Se o board vinha
        // de pasta, ele nao esta aqui; se ja era da raiz, esta incluso.
        const previousItems = buildRootItems(state, wsId).map((it) => ({ kind: it.kind, id: it.id }));
        // Se o board vinha de dentro de uma pasta, primeiro tira-o da pasta
        // (o reorder-root exige folder_id IS NULL para itens do tipo board).
        if (cameFromFolder) {
          try {
            await moveBoardToWorkspaceFolder(activeData.boardId, null);
          } catch (err) {
            dialog.alert('Erro', err.message || 'Falha ao mover quadro');
            return;
          }
        }
        const freshState = useBoardStore.getState();
        const rootItems = buildRootItems(freshState, wsId);
        const newOrder = reorderRootList(rootItems, { kind: 'board', id: activeData.boardId }, 'board', overData.boardId, position);
        if (!newOrder) return;
        try {
          await reorderRoot({ workspaceId: wsId, items: newOrder.map((it) => ({ kind: it.kind, id: it.id })) });
          showToast({
            message: 'Quadro reordenado',
            onUndo: async () => {
              try {
                await reorderRoot({ workspaceId: wsId, items: previousItems });
                if (cameFromFolder) await moveBoardToWorkspaceFolder(activeData.boardId, originalFolderId);
              } catch {}
            },
          });
        } catch (err) {
          dialog.alert('Erro', err.message || 'Falha ao reordenar quadros');
        }
        if (draggingSelected) state.clearBoardSelection();
        lastHalfByBoardRef.current.clear();
        return;
      }

      // ----- Board DENTRO de pasta: reorder por escopo (inalterado) -----
      const previousOrder = state.boards
        .filter((b) => b.workspace_id === overBoard.workspace_id && (b.folder_id ?? null) === targetFolderId)
        .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) || a.id - b.id)
        .map((b) => b.id);

      // Constrói lista ordenada do escopo destino (sem o board ativo, se já estiver nele)
      const scopeBoards = state.boards
        .filter((b) => b.workspace_id === overBoard.workspace_id && (b.folder_id ?? null) === targetFolderId && b.id !== activeData.boardId)
        .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) || a.id - b.id);
      const overIndex = scopeBoards.findIndex((b) => b.id === overData.boardId);
      if (overIndex < 0) return;
      const insertIndex = position === 'before' ? overIndex : overIndex + 1;
      const newOrder = [
        ...scopeBoards.slice(0, insertIndex).map((b) => b.id),
        activeData.boardId,
        ...scopeBoards.slice(insertIndex).map((b) => b.id),
      ];
      try {
        await reorderBoards({
          workspaceId: overBoard.workspace_id,
          folderId: targetFolderId,
          orderedIds: newOrder,
        });
        showToast({
          message: 'Quadro reordenado',
          onUndo: async () => {
            try {
              await reorderBoards({
                workspaceId: overBoard.workspace_id,
                folderId: targetFolderId,
                orderedIds: previousOrder,
              });
            } catch {}
          },
        });
      } catch (err) {
        dialog.alert('Erro', err.message || 'Falha ao reordenar quadros');
      }
    }

    if (draggingSelected) state.clearBoardSelection();
    lastHalfByBoardRef.current.clear();
  };

  // Captura cursor Y global enquanto há drag ativo; usado em computeHalf.
  useEffect(() => {
    if (!activeDragBoard && !activeDragFolder) return;
    const onMove = (e) => { pointerYRef.current = e.clientY; };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [activeDragBoard, activeDragFolder]);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <TopHeader />
      <div className="flex flex-1 overflow-hidden">
      <aside
        style={{ width: `${sidebarWidth}px` }}
        className={`relative bg-sidebar flex flex-col border-r border-border shrink-0 ${
          resizing ? '' : 'transition-[width] duration-200'
        }`}
      >
        <button
          onClick={toggleCollapsed}
          title={collapsed ? 'Abrir navegação' : 'Fechar navegação'}
          className="absolute -right-3 top-4 z-30 w-6 h-6 rounded-full bg-sidebar border border-border flex items-center justify-center text-text-secondary hover:text-accent hover:border-accent transition-colors shadow"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
        <div
          onPointerDown={(e) => { e.preventDefault(); setResizing(true); }}
          onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
          title="Arrastar para redimensionar (duplo clique para redefinir)"
          className={`absolute right-0 top-0 bottom-0 w-1.5 z-20 cursor-col-resize transition-colors ${
            resizing ? 'bg-accent/40' : 'hover:bg-accent/30'
          }`}
        />


        {/* Header — não scrolla */}
        <div className="shrink-0 py-3">
          <NavItem icon={Home} label="Início" collapsed={collapsed} active={location.pathname === '/' && !activeBoardId} onClick={() => navigate('/')} />
          <NavItem icon={BarChart3} label="Métricas" collapsed={collapsed} active={location.pathname === '/metrics/people'} onClick={() => navigate('/metrics/people')} />
          {isOrgAdmin(user) && (
            <NavItem icon={Trash2} label="Lixeira" collapsed={collapsed} active={location.pathname === '/trash'} onClick={() => navigate('/trash')} />
          )}

          {!collapsed && (
            <div className="mt-4">
              <FavoritesSection
                activeBoardId={activeBoardId ? Number(activeBoardId) : null}
                onNavigate={navigate}
              />
              <RecentSection
                activeBoardId={activeBoardId ? Number(activeBoardId) : null}
                onNavigate={navigate}
              />
              <div className="flex items-center justify-between px-4 pt-1 pb-2">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Áreas de trabalho
                </span>
                {isOrgAdmin(user) && (
                  <div ref={plusMenuRef} className="relative">
                    <button
                      onClick={() => setPlusMenuOpen((v) => !v)}
                      className="p-1 rounded bg-accent text-white hover:bg-accent/80 transition-colors"
                      title="Adicionar"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    {plusMenuOpen && (
                      <div className="absolute right-0 top-full mt-1 z-40 w-52 bg-card border border-border rounded-lg shadow-xl py-1">
                        <button
                          onClick={handleNewBoard}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Novo quadro
                        </button>
                        <button
                          onClick={handleNewFolder}
                          disabled={!activeWsId}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <FolderPlus className="w-3.5 h-3.5" />
                          Nova pasta
                        </button>
                        <div className="my-1 border-t border-border/50" />
                        <button
                          onClick={handleNewWorkspace}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors"
                        >
                          <LayoutGrid className="w-3.5 h-3.5" />
                          Nova área de trabalho
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <WorkspaceDropdown
                userId={user?.id}
                activeId={activeWsId}
                onChange={changeActiveWs}
              />
            </div>
          )}

          {collapsed && isOrgAdmin(user) && (
            <div className="flex flex-col items-center gap-1 mt-2">
              <button
                onClick={() => setModalOpen(true)}
                className="p-2 rounded hover:bg-white/5 text-text-secondary transition-colors"
                title="Novo Quadro"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Lista scrollable — lista unica da raiz (pastas + boards intercalados) */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {!collapsed && activeWsId && (
            <DndContext sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDragCancel={handleDragCancel}>
              <WorkspaceSection
                workspace={workspaces.find((w) => w.id === activeWsId)}
                boards={boards.filter((b) => b.workspace_id === activeWsId)}
                activeBoardId={activeBoardId ? Number(activeBoardId) : null}
                onFetchBoards={fetchBoards}
                onNavigate={navigate}
                canManageBoards={isOrgAdmin(user)}
              />
              <DragOverlay dropAnimation={null}>
                {activeDragBoard && (
                  <div
                    className="flex items-center pr-2 py-1.5 text-sm text-text-primary bg-sidebar rounded shadow-xl border border-accent/50 cursor-grabbing select-none"
                    style={{ paddingLeft: '36px', width: `${sidebarWidth - 16}px`, opacity: 0.75 }}
                  >
                    <span className="truncate flex-1">{activeDragBoard.name}</span>
                  </div>
                )}
                {activeDragFolder && (
                  <div
                    className="flex items-center gap-1.5 pr-2 py-1 text-xs font-medium text-text-primary bg-sidebar rounded shadow-xl border border-accent/50 cursor-grabbing select-none"
                    style={{ paddingLeft: '12px', width: `${sidebarWidth - 16}px`, opacity: 0.8 }}
                  >
                    <Folder className="w-3.5 h-3.5 text-accent/80 shrink-0" />
                    <span className="truncate flex-1">{activeDragFolder.name}</span>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          )}
        </div>

      </aside>

      <main className="flex-1 bg-main overflow-auto relative">
        <Outlet />
      </main>
      </div>

      {modalOpen && (
        <NewBoardModal
          workspaces={workspaces}
          defaultWorkspaceId={activeWsId}
          onClose={() => setModalOpen(false)}
          onNavigate={navigate}
        />
      )}
    </div>
  );
}

function WorkspaceSection({ workspace, boards, activeBoardId, onFetchBoards, onNavigate, canManageBoards }) {
  const user = useAuthStore((s) => s.user);
  const { setNodeRef: setRootDropRef, isOver: isOverRoot } = useDroppable({
    id: `workspace-root-${workspace.id}`,
    data: { type: 'workspaceRoot', workspaceId: workspace.id },
  });
  const { workspaceFolders, selectedBoardIds } = useBoardStore(
    useShallow((s) => ({
      workspaceFolders: s.workspaceFolders,
      selectedBoardIds: s.selectedBoardIds,
    }))
  );
  const duplicateBoard = useBoardStore((s) => s.duplicateBoard);
  const archiveBoard = useBoardStore((s) => s.archiveBoard);
  const fetchWorkspaceFolders = useBoardStore((s) => s.fetchWorkspaceFolders);
  const moveBoardToWorkspaceFolder = useBoardStore((s) => s.moveBoardToWorkspaceFolder);
  const toggleBoardSelection = useBoardStore((s) => s.toggleBoardSelection);
  const clearBoardSelection = useBoardStore((s) => s.clearBoardSelection);
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const moveMenuRef = useRef(null);

  useEffect(() => {
    if (workspace?.id) {
      onFetchBoards(workspace.id);
      fetchWorkspaceFolders(workspace.id);
    }
  }, [workspace?.id]);

  useEffect(() => {
    clearBoardSelection();
  }, [workspace?.id]);

  useEffect(() => {
    if (!moveMenuOpen) return;
    const h = (e) => {
      if (moveMenuRef.current && !moveMenuRef.current.contains(e.target)) setMoveMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [moveMenuOpen]);

  if (!workspace) return null;

  const folders = workspaceFolders.filter((f) => f.workspace_id === workspace.id);
  const folderIds = new Set(folders.map((f) => f.id));
  const boardsByFolder = new Map();
  const rootBoards = [];
  const byPos = (a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) || a.id - b.id;
  for (const b of boards) {
    if (b.folder_id && folderIds.has(b.folder_id)) {
      if (!boardsByFolder.has(b.folder_id)) boardsByFolder.set(b.folder_id, []);
      boardsByFolder.get(b.folder_id).push(b);
    } else {
      rootBoards.push(b);
    }
  }
  rootBoards.sort(byPos);
  for (const [fid, arr] of boardsByFolder) boardsByFolder.set(fid, arr.slice().sort(byPos));

  // Lista UNICA da raiz: pastas + boards soltos num mesmo espaco de position,
  // mesclados e ordenados. Boards e pastas se intercalam pela position.
  const rootItems = [
    ...folders.map((f) => ({ kind: 'folder', id: f.id, position: f.position, data: f })),
    ...rootBoards.map((b) => ({ kind: 'board', id: b.id, position: b.position, data: b })),
  ].sort((a, b) => {
    const pa = a.position ?? Number.MAX_SAFE_INTEGER;
    const pb = b.position ?? Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    // desempate estavel: pastas antes de boards na mesma position, depois por id
    if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
    return a.id - b.id;
  });

  const selectedSet = new Set(selectedBoardIds);
  const anySelected = selectedSet.size > 0;

  const bulkMove = async (folderId) => {
    setMoveMenuOpen(false);
    const ids = [...selectedSet];
    for (const id of ids) await moveBoardToWorkspaceFolder(id, folderId);
    clearBoardSelection();
  };

  const bulkArchive = async () => {
    const ids = [...selectedSet];
    const label = ids.length === 1 ? '1 quadro' : `${ids.length} quadros`;
    if (!(await dialog.confirm('Mover para a lixeira', `Mover ${label} para a lixeira?`, { confirmLabel: 'Mover', danger: true }))) return;
    for (const id of ids) await archiveBoard(id);
    if (activeBoardId && ids.includes(activeBoardId)) onNavigate('/');
    clearBoardSelection();
  };

  const selectionProps = canManageBoards
    ? { selectable: true, anySelected, onToggleSelect: toggleBoardSelection }
    : {};

  return (
    <div className="mt-2 mb-3">
      {rootItems.length === 0 && (
        <div className="px-4 py-2 text-[11px] text-text-secondary/60 italic">
          Nenhum quadro nesta área
        </div>
      )}
      {rootItems.map((item) => {
        if (item.kind === 'folder') {
          return (
            <WorkspaceFolder
              key={`folder-${item.id}`}
              folder={item.data}
              boards={boardsByFolder.get(item.id) || []}
              activeBoardId={activeBoardId}
              onNavigate={onNavigate}
              canManage={canManageBoards}
              selectionProps={{
                ...selectionProps,
                selectedIds: selectedSet,
              }}
            />
          );
        }
        const board = item.data;
        // Menu de opcoes (Renomear/Mover/Duplicar/Excluir=arquivar) por board:
        // dono do quadro (my_level==='owner') ou admin da org. Fallback sem
        // my_level (backend M1a) = regra antiga, so isOrgAdmin (canManageBoards).
        // Dois niveis: quem gerencia o quadro (renomear/mover/duplicar/
        // arquivar) e quem so pode definir a senha de abertura — o dono
        // nao-admin do quadro, com nivel 'owner' mas papel sem
        // board.archive/board.delete. O menu abre para os dois; os
        // handlers de duplicar/excluir so vao para o primeiro, e o proprio
        // BoardItemMenu esconde renomear/mover do segundo.
        const canManageThisBoard = canManageBoardEntry(board, user);
        const canProtectThisBoard = canManageBoardProtection(board, user);
        const showBoardMenu = canManageThisBoard || canProtectThisBoard;
        const menuHandlers = canManageThisBoard ? {
          onDuplicate: async () => {
            const dup = await duplicateBoard(board.id);
            if (dup) {
              await onFetchBoards(workspace.id);
              onNavigate(`/board/${dup.id}`);
            }
          },
          onDelete: async () => {
            if (await dialog.confirm('Mover para a lixeira', `Mover "${board.name}" para a lixeira?`, { confirmLabel: 'Mover', danger: true })) {
              await archiveBoard(board.id);
              await onFetchBoards(workspace.id);
              if (board.id === activeBoardId) onNavigate('/');
            }
          },
        } : {};
        return (
          <DraggableBoardItem
            key={`board-${board.id}`}
            board={board}
            active={board.id === activeBoardId}
            onClick={() => onNavigate(`/board/${board.id}`)}
            {...selectionProps}
            selected={selectedSet.has(board.id)}
            renderContextMenu={showBoardMenu ? ({ anchorPoint, onClose }) => (
              <BoardItemMenu board={board} {...menuHandlers} anchorPoint={anchorPoint} onClose={onClose} hideButton />
            ) : undefined}
          >
            {showBoardMenu && !anySelected && (
              <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                <BoardItemMenu board={board} {...menuHandlers} />
              </div>
            )}
          </DraggableBoardItem>
        );
      })}

      {/* Drop zone explícita fora de pasta — apenas no fim */}
      <div ref={setRootDropRef} className="mt-6 mx-2" aria-hidden="true">
        {isOverRoot && (
          <>
            <div className="h-1 bg-accent rounded-full" />
            <div className="h-7 mt-0.5 rounded-md border border-dashed border-accent/50 bg-accent/5" />
          </>
        )}
        {!isOverRoot && (
          <div className="h-20" />
        )}
      </div>

      {anySelected && (
        <div ref={moveMenuRef} className="sticky bottom-0 mt-3 px-3 py-2 bg-card border-t border-border flex items-center gap-1.5 z-20 shadow-[0_-4px_12px_rgba(0,0,0,0.25)]">
          {moveMenuOpen && (
            <div className="absolute left-2 right-2 bottom-full mb-1 bg-card border border-border rounded-lg shadow-xl py-1 max-h-64 overflow-y-auto">
              <div className="px-3 py-1 text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
                Mover para
              </div>
              <button
                onClick={() => bulkMove(null)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
              >
                <X className="w-3.5 h-3.5" />
                Sem pasta
              </button>
              {folders.length > 0 && <div className="my-1 border-t border-border" />}
              {folders.map((f) => (
                <button
                  key={f.id}
                  onClick={() => bulkMove(f.id)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
                >
                  <Folder className="w-3.5 h-3.5 text-accent/80 shrink-0" />
                  <span className="truncate">{f.name}</span>
                </button>
              ))}
            </div>
          )}
          <span className="text-xs text-text-primary font-medium">
            {selectedSet.size} selecionado{selectedSet.size > 1 ? 's' : ''}
          </span>
          <div className="flex-1" />
          <button
            onClick={() => setMoveMenuOpen((v) => !v)}
            className="p-1.5 rounded hover:bg-white/10 text-text-primary transition-colors"
            title="Mover para pasta"
          >
            <FolderInput className="w-4 h-4" />
          </button>
          <button
            onClick={bulkArchive}
            className="p-1.5 rounded hover:bg-danger/10 text-danger transition-colors"
            title="Mover para lixeira"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={clearBoardSelection}
            className="p-1.5 rounded hover:bg-white/10 text-text-secondary transition-colors"
            title="Cancelar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function NewBoardModal({ workspaces, defaultWorkspaceId, onClose, onNavigate }) {
  const [name, setName] = useState('');
  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceId || workspaces[0]?.id || '');
  const { createBoard, fetchBoards } = useBoardStore();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !workspaceId) return;
    try {
      const board = await createBoard(name.trim(), Number(workspaceId));
      await fetchBoards(Number(workspaceId));
      onClose();
      onNavigate(`/board/${board.id}`);
    } catch {
      // ignore
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Novo Quadro</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit}>
          <label className="block text-sm text-text-secondary mb-1">Nome</label>
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-main border border-border rounded px-3 py-2 text-sm text-text-primary outline-none focus:border-accent mb-4"
            placeholder="Nome do quadro"
          />
          <label className="block text-sm text-text-secondary mb-1">Workspace</label>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            className="w-full bg-main border border-border rounded px-3 py-2 text-sm text-text-primary outline-none focus:border-accent mb-6"
          >
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </select>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent text-white text-sm rounded hover:bg-accent/80 transition-colors"
            >
              Criar Board
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NavItem({ icon: Icon, label, collapsed, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
        active
          ? 'text-text-primary bg-white/10'
          : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
      } ${collapsed ? 'justify-center' : ''}`}
      title={collapsed ? label : undefined}
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </button>
  );
}
